// @react
import { useEffect, useContext } from 'react'
// @libraries
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { vsprintf } from 'sprintf-js'
// @common
import strings from 'lib/constants/strings'
import { SnackbarContext } from 'modules/common/context/snackbar'
// @types
import {
  GetConstantsQueryResult,
  GetUserQuery,
  GetUserQueryResult,
  UserJobUpdateMutation,
  UserJobUpdateMutationVariables,
} from 'lib/graphql/graphqlTypes'
import { WorkFormValues } from 'modules/common/forms/types/work'
import { SnackbarTypes } from '@bit/atd.web.snackbar'
// @graphQL
import { ApolloQueryResult, useMutation } from '@apollo/client'
import { USER_JOB_UPDATE } from 'graphql/mutations/user.mutation'
// @components
// @module components
import { validationSchema } from 'modules/common/forms/validations/work'

type PropsType = {
  data?: GetUserQueryResult['data']
  constants?: GetConstantsQueryResult['data']
  isLoading: boolean
  refetch: () => Promise<ApolloQueryResult<GetUserQuery>>
}

const useWorkFormWrapper = ({ data: userData, refetch }: PropsType) => {
  /******************************************
   * STATE
   ******************************************/

  /******************************************
   * HOOKS
   ******************************************/

  const snackbar = useContext(SnackbarContext)

  // React Hook Form
  const formMethods = useForm<WorkFormValues>({
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    resolver: yupResolver(validationSchema),
    defaultValues: {
      industry: '',
      role: '',
      jobTitle: '',
      companyName: '',
      companySize: '',
      trainingBudget: '',
      directReportCount: '',
    },
  })
  const { control, reset } = formMethods
  const user = userData?.user

  useEffect(() => {
    if (user && reset) {
      reset({
        industry: user.job.jobIndustry || '',
        role: user.job.jobFunctionCode || '',
        jobTitle: user.job.jobTitle || '',
        companyName: user.job.companyName || '',
        companySize: user.job.companySize || '',
        trainingBudget: user.job.trainingBudget || '',
        directReportCount: user.job.directReportCount || '',
      })
    }
  }, [reset, user])

  /****************************************
   * GQL HOOKS
   *****************************************/

  // Mutations
  const [updateUserJob] = useMutation<
    UserJobUpdateMutation,
    UserJobUpdateMutationVariables
  >(USER_JOB_UPDATE)

  /******************************************
   * FUNCTIONS
   ******************************************/

  /**
   *
   * @param data
   */
  const userJobUpdateHandler = async (
    data: WorkFormValues,
  ): Promise<boolean> => {
    const { errors: mutationErrors } = await updateUserJob({
      variables: {
        input: {
          jobIndustry: data.industry,
          jobFunctionCode: data.role,
          jobTitle: data.jobTitle,
          companyName: data.companyName,
          companySize: data.companySize,
          trainingBudget: data.trainingBudget,
          directReportCount: data.directReportCount,
        },
      },
    })

    if (!mutationErrors) {
      snackbar.setMessage({
        type: SnackbarTypes.SUCCESS,
        message: vsprintf(strings.SUCCESS.GENERIC_VARIABLE_UPDATE_SUCCESS, [
          'Work',
        ]),
        show: true,
      })
      await refetch()
      return true
    } else {
      snackbar.setMessage({
        type: SnackbarTypes.ERROR,
        message: strings.ERRORS.GENERIC_UPDATE_ERROR,
        show: true,
      })
      console.log('mutation error', mutationErrors)
    }
    return false
  }

  /******************************************
   * FORM HANDLERS
   ******************************************/

  /******************************************
   * RENDER
   ******************************************/

  return {
    formMethods,
    control,
    userJobUpdateHandler,
  }
}

export default useWorkFormWrapper
